/**
 * AgencyPipeline.jsx — cross-client kanban for Sprint 5 (Module 08).
 *
 * Pulls every request across every active engagement and renders 5 columns:
 * New · In progress · Review · Revisions · Approved.
 * Click any card → opens the same RequestDetailModal we already use in the
 * per-engagement Pipeline tab.  Filters: client, platform, priority.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { Funnel, ArrowRight, Clock, X } from '@phosphor-icons/react';

const ACCENT = '#6366f1';

const STATUSES = [
  { id: 'new',         label: 'New',         color: '#64748b' },
  { id: 'in_progress', label: 'In progress', color: '#6366f1' },
  { id: 'review',      label: 'In review',   color: '#f59e0b' },
  { id: 'revisions',   label: 'Revisions',   color: '#ef4444' },
  { id: 'approved',    label: 'Approved',    color: '#10b981' },
];

const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';

const AgencyPipeline = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ engagement_id: '', platform: '', priority: '' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/agency/pipeline');
        if (!cancelled) {
          setRequests(r.data.requests || []);
          setEngagements(r.data.engagements || []);
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => requests.filter((r) =>
    (!filters.engagement_id || r.engagement_id === filters.engagement_id) &&
    (!filters.platform || r.platform === filters.platform) &&
    (!filters.priority || r.priority === filters.priority)
  ), [requests, filters]);

  const platforms = useMemo(() => Array.from(new Set(requests.map((r) => r.platform).filter(Boolean))).sort(), [requests]);

  const grouped = useMemo(() => {
    const g = {}; STATUSES.forEach((s) => { g[s.id] = []; });
    filtered.forEach((r) => { (g[r.status] || g.new).push(r); });
    return g;
  }, [filtered]);

  const clearFilters = () => setFilters({ engagement_id: '', platform: '', priority: '' });
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="agency-pipeline">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: ACCENT }}>Command Centre</div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary leading-[1.05] mb-2" style={{ letterSpacing: '-0.02em' }}>
          Cross-client pipeline
        </h1>
        <p className="text-sm text-text-secondary">Every request across every engagement, in one board.</p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 bg-surface border border-border rounded-xl p-3 mb-5">
        <Funnel size={12} weight="bold" className="text-text-muted ml-1" />
        <select value={filters.engagement_id} onChange={(e) => setFilters((p) => ({ ...p, engagement_id: e.target.value }))} data-testid="agency-pipeline-filter-client" className="bg-background border border-border rounded-lg px-3 py-1.5 text-xs">
          <option value="">All clients</option>
          {engagements.map((e) => (
            <option key={e.id} value={e.id}>{e.founder_startup_name || e.founder_full_name}</option>
          ))}
        </select>
        <select value={filters.platform} onChange={(e) => setFilters((p) => ({ ...p, platform: e.target.value }))} data-testid="agency-pipeline-filter-platform" className="bg-background border border-border rounded-lg px-3 py-1.5 text-xs">
          <option value="">All platforms</option>
          {platforms.map((p) => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={filters.priority} onChange={(e) => setFilters((p) => ({ ...p, priority: e.target.value }))} data-testid="agency-pipeline-filter-priority" className="bg-background border border-border rounded-lg px-3 py-1.5 text-xs">
          <option value="">All priorities</option>
          {['urgent', 'high', 'normal', 'low'].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        {activeFilterCount > 0 && (
          <button onClick={clearFilters} className="text-xs text-text-secondary hover:text-text-primary inline-flex items-center gap-1" data-testid="agency-pipeline-filter-clear">
            <X size={11} weight="bold" /> Clear
          </button>
        )}
        <div className="ml-auto text-xs text-text-muted font-mono">{filtered.length} request{filtered.length === 1 ? '' : 's'}</div>
      </div>

      {loading ? <div className="text-sm text-text-muted">Loading…</div> : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="agency-pipeline-kanban">
          {STATUSES.map((s) => (
            <div key={s.id} className="bg-surface border border-border rounded-xl p-3 min-h-[200px]">
              <div className="flex items-center justify-between mb-3">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-text-primary">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} /> {s.label}
                </span>
                <span className="text-[10px] font-mono text-text-muted">{grouped[s.id].length}</span>
              </div>
              <div className="space-y-2">
                {grouped[s.id].map((r) => (
                  <button
                    key={r.id}
                    onClick={() => navigate(`/agency/engagements/${r.engagement_id}?req=${r.id}`)}
                    data-testid={`agency-pipeline-card-${r.id}`}
                    className="text-left w-full bg-background border border-border rounded-lg p-3 hover:border-text-primary transition-colors"
                  >
                    <div className="text-[10px] uppercase tracking-wider font-bold mb-1 truncate" style={{ color: ACCENT }}>{r.founder_label}</div>
                    <div className="text-sm font-medium text-text-primary line-clamp-2 mb-1">{r.title}</div>
                    <div className="flex items-center justify-between text-[10px] text-text-muted">
                      {r.platform && <span className="capitalize truncate">{r.platform.replace(/_/g, ' ')}</span>}
                      {r.due_date && <span className="inline-flex items-center gap-1 flex-shrink-0"><Clock size={9} weight="bold" /> {fmtDate(r.due_date)}</span>}
                    </div>
                    {r.priority && r.priority !== 'normal' && (
                      <span className="inline-block mt-2 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: r.priority === 'urgent' ? '#ef444422' : '#f59e0b22', color: r.priority === 'urgent' ? '#ef4444' : '#f59e0b' }}>{r.priority}</span>
                    )}
                  </button>
                ))}
                {grouped[s.id].length === 0 && <div className="text-[11px] text-text-muted/60 italic py-2 text-center">empty</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-text-muted mt-3 inline-flex items-center gap-1">Click any card to jump to the engagement workspace <ArrowRight size={10} weight="bold" /></p>
    </div>
  );
};

export default AgencyPipeline;
